import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { SnackbarAlert } from "../common/HelperFunc";
import { useProjectApi } from "../../hooks/useProjectApi";
import * as message from "../../constants/messages";

// 테이블 데이터 조회
export const  useGetTableData = (
  updateStatus,
  endpoint,
  groupCode,
  projectCode,
) => {
  const dataName = "data"; // 데이터 혼동을 막기 위해서 사용
  const api = useProjectApi(groupCode, projectCode);
  const { data, ...rest } = useQuery({
    queryKey: [updateStatus, endpoint],
    queryFn: async () => {
      const response = await api.get(endpoint);
      return response.data;
    },
    placeholderData: [],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    cacheTime: 0,
  });
  return { [dataName]: data || [], ...rest };
};

// 테이블 데이터 추가
export const useCreateTableData = (
  updateStatus,
  endpoint,
  groupCode,
  projectCode
) => {
  const queryClient = useQueryClient();
  const api = useProjectApi(groupCode, projectCode);
  return useMutation({
    mutationKey: [updateStatus, endpoint],
    mutationFn: async (arr) => {
      console.log("mutation에 전달된 데이터:", arr);
      console.log("엔드포인트:", endpoint);
      const response = await api.post(endpoint, arr);
      console.log("서버 응답:", response);
      if (response.status !== 200) {
        throw new Error("Failed to add sth");
      }
      return response; // data, status
      //return response.data;
      //return response.status;
    },
    onSuccess: async () => {
      // await queryClient.invalidateQueries({
      //   predicate: (query) => query.queryKey[0] === updateStatus,
      // });
      await queryClient.refetchQueries({ // 해당 쿼리 키를 가진 쿼리를 다시 가져옵니다. 
        predicate: (query) => query.queryKey[0] === updateStatus,
        type: "active",
      });
      //toast.success(message.completeAdd);
    },
    onError: () => {
      toast.error(message.errorCreate);
    },
  });
};

// 테이블 데이터 수정
export const usePutTableData = (
  updateStatus,
  endpoint,
  groupCode,
  projectCode
) => {
  const queryClient = useQueryClient();
  const api = useProjectApi(groupCode, projectCode);
  return useMutation({
    mutationKey: [updateStatus, endpoint],
    mutationFn: async (arr) => {
      const response = await api.put(endpoint, arr);
      if (response.status !== 200) {
        throw new Error("Failed to edit sth");
      }
      return response; // data, status
      //return response.status;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([updateStatus]);
      //toast.success(message.editComplete);
    },
    onError: (error) => {
      console.log(error);
      toast.error(message.editError);
    },
  });
};

// 테이블 데이터 삭제
export const useDeleteTableData = (
  updateStatus,
  baseEndpoint,
  groupCode,
  projectCode
) => {
  const queryClient = useQueryClient(); // react-query의 쿼리 클라이언트를 사용하여 캐시를 관리합니다.
  const api = useProjectApi(groupCode, projectCode); // 프로젝트 API를 호출하는 함수를 가져옵니다.
  return useMutation({
    mutationKey: [updateStatus, baseEndpoint], // 변이 작업을 식별하는 키입니다.
    mutationFn: async ({ deleteParam, param, ...extra }) => { // 실제 삭제 작업을 수행하는 비동기 함수입니다.
      const endpoint = `${baseEndpoint}?${deleteParam}=${param}`; // 삭제 요청을 보낼 엔드포인트를 구성합니다.
      const response = await api.delete(endpoint, { 
        data: { ...extra } // 추가 데이터를 요청 본문에 포함시킬 수 있습니다.(body)
       }); // API 호출을 통해 데이터를 삭제합니다.
      return response; // data, status, 서버 응답 데이터를 반환합니다.
    },
    onSuccess: async () => { // 삭제 작업이 성공했을 때 호출되는 콜백 함수입니다.
      await queryClient.invalidateQueries({ 
        queryKey: [updateStatus], // 해당 쿼리 키를 가진 쿼리를 무효화합니다.
        exact: true, // 정확히 일치하는 쿼리만 무효화합니다.
        refetchType: "all", // 모든 쿼리를 다시 가져옵니다.
      });
      await queryClient.refetchQueries([updateStatus]); // 해당 쿼리 키를 가진 쿼리를 다시 가져옵니다.
      console.log("성공적으로 데이터를 가져옮");
      //toast.success(message.deleteSuccess); // 성공 메시지를 표시합니다.
    },
    onError: (error) => { // 삭제 작업이 실패했을 때 호출되는 콜백 함수입니다.
      console.error("데이터 가져오는 중 오류 발생:", error); // 오류를 콘솔에 출력합니다.
      toast.error(message.deleteError); // 오류 메시지를 표시합니다.
    },
  });
};

// 새로운 삭제 훅 (query string + body)
// param: 쿼리 스트링으로 보낼 값들 (예: { sitCategory: SIT_CATEGORY })
// body: DELETE body (서버가 허용할 때만) (예: { deleteData: [...] })
export const useDeleteTableDataNew = (updateStatus, baseEndpoint, groupCode, projectCode) => {
  const queryClient = useQueryClient();
  const api = useProjectApi(groupCode, projectCode);

  return useMutation({
    mutationKey: [updateStatus, baseEndpoint],
    // payload: { params: {}, body: {} }
    mutationFn: async ({ params = {}, body = {} }) => {
      const res = await api.delete(baseEndpoint, {
        params, // 쿼리스트링으로 보낼 값들
        data: body, // DELETE body (서버가 허용할 때만)
      });
      return res;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ 
        queryKey: [updateStatus], 
        exact: true,
        //refetchType: "all",
      });
      // 아래 한 줄은 대부분 불필요(중복). 필요할 때만.
      await queryClient.refetchQueries({ queryKey: [updateStatus] });
      console.log("성공적으로 데이터를 가져옮");
    },
    onError: (error) => {
      console.error("데이터 가져오는 중 오류 발생:", error);
      toast.error(message.deleteError);
    },
  });
};

// 쿼리 스트링으로 테이블 데이터 조회
export const  useGetTableDataInQueryString = (
  updateStatus,
  endpoint,
  groupCode,
  projectCode,
  qeuryString,
  enabled = true,
) => {
  const dataName = "data"; // 데이터 혼동을 막기 위해서 사용
  const api = useProjectApi(groupCode, projectCode);
  const { data, ...rest } = useQuery({
    //queryKey: [updateStatus, endpoint],
    queryKey: [updateStatus, endpoint, JSON.stringify(qeuryString)], // 쿼리 스트링 변경할 때마다 조회
    queryFn: async () => {
      const response = await api.get(endpoint, qeuryString);
      return response.data;
    },
    placeholderData: [],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    cacheTime: 0,
    enabled,
  });
  return { [dataName]: data || [], ...rest };
};

//--------------------------------------
export const usePutTableDataReset = (
  updateStatus,
  endpoint,
  groupCode,
  projectCode
) => {
  const queryClient = useQueryClient();
  const api = useProjectApi(groupCode, projectCode);

  return useMutation({
    mutationKey: [updateStatus, endpoint],
    mutationFn: async (arr) => {
      const response = await api.put(endpoint, arr);
      if (response.status !== 200) {
        throw new Error("Failed to edit sth");
      }
      return response.status;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([updateStatus]);
    },
    onError: (error) => {
      console.log(error);
      toast.error(message.resetError);
    },
  });
};
